import { useCallback, useMemo, useRef, useState } from "react";

export function usePreviewAudioController() {
  const [isMuted, setIsMuted] = useState(true);
  const activeMediaElementRef = useRef<HTMLAudioElement | null>(null);
  const isMutedRef = useRef(isMuted);

  const setMuted = useCallback((nextIsMuted: boolean) => {
    isMutedRef.current = nextIsMuted;
    setIsMuted(nextIsMuted);

    if (activeMediaElementRef.current) {
      activeMediaElementRef.current.muted = nextIsMuted;
    }
  }, []);

  const setActiveMediaElement = useCallback(
    (mediaElement: HTMLAudioElement | null, previousMediaElement: HTMLAudioElement | null = null) => {
      if (mediaElement) {
        activeMediaElementRef.current = mediaElement;
        mediaElement.muted = isMutedRef.current;
        return;
      }

      if (activeMediaElementRef.current === previousMediaElement) {
        activeMediaElementRef.current = null;
      }
    },
    [],
  );

  const isActiveMediaElement = useCallback(
    (mediaElement: HTMLAudioElement) => activeMediaElementRef.current === mediaElement,
    [],
  );

  const mute = useCallback(() => {
    setMuted(true);
  }, [setMuted]);

  const resetForTabChange = useCallback(() => {
    const mediaElement = activeMediaElementRef.current;

    mute();
    mediaElement?.pause();
    activeMediaElementRef.current = null;
  }, [mute]);

  return useMemo(
    () => ({
      isMuted,
      isActiveMediaElement,
      mute,
      resetForTabChange,
      setActiveMediaElement,
      setMuted,
    }),
    [isActiveMediaElement, isMuted, mute, resetForTabChange, setActiveMediaElement, setMuted],
  );
}

export type PreviewAudioController = ReturnType<typeof usePreviewAudioController>;
